import React, { useEffect, useState } from "react";
import { Row, Col, Button, Modal, ModalBody, Spinner, Label, Input } from "reactstrap";
import { Icon, RSelect } from "../../../../components/Component";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { HttpStatus, PropertyCardStatus, uploadManyToS3 } from "../../../../utils/envConfig";
import uuid from "react-uuid";
import compressImage from "../../../../utils/compression/compress";
import { showToast } from '../../../../utils/toast/toast';
import { useSelector } from "react-redux";
import { loggedInAgentSelector } from "../../../../redux/selectors/user";
import { hasValue, safeParseNumber } from "../../../../utils/Utils";

const ConvertToPocketListingModal = ({
  cardId,
  payload,
  isOpen,
  toggleFn,
  updateCallback = () => {},
}) => {
  const axios = useAxiosPrivate();
  const [selectedPurpose, setSelectedPurpose] = useState(payload?.purpose ?? null);
  const [selectedLead, setSelectedLead] = useState(payload?.lead ?? null);
  const [askingPrice, setAskingPrice] = useState(payload?.askingPrice ?? null);
  const [agencyValuation, setAgencyValuation] = useState(payload?.agentValutionPrice ?? null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [isInitialLeadsLoading, setIsInitialLeadsLoading] =
    useState(true);
  const [leadsOptions, setLeadsOptions] = useState([]);

  const { agentLeads, leadsLoading } = useSelector((state) => ({
    agentLeads: state?.user?.users?.findUsersOutput,
    leadsLoading: state?.user?.leadsLoading,
  }));

  useEffect(() => {
    if (agentLeads) {
      setLeadsOptions(
        agentLeads?.map((lead) => ({
          ...lead,
          label: `${lead.first_name} ${lead.last_name ?? ""}`,
          value: lead.id,
        }))
      );
      setIsInitialLeadsLoading(false);
    }
  }, [agentLeads, payload?.currentAgent]);

  const authAgent = useSelector(loggedInAgentSelector);

  const fetchLeadsByName = async (inputValue) => {
    try {
      let url = `/search/user/filter?search=${inputValue}&active=true`;
      if (authAgent) {
        url += `&agent_id=${authAgent?.id}`;
      } else if (payload?.currentAgent) {
        url += `&agent_id=${payload?.currentAgent?.value}`;
      }

      const response = await axios.get(url);
      const data = response.data?.findUsersOutput?.map((lead) => ({
        value: lead.id,
        label: `${lead.first_name} ${lead.last_name ?? ""}`,
        ...lead,
      }));
      setLeadsOptions(data);
      return data;
    } catch (error) {
      console.error("Error fetching users:", error);
      return [];
    } finally {
    }
  };

  const updateCard = async () => {
    try {
      if(!selectedPurpose){
       return toast.error("Purpose is a required field.")
      }
      setUpdateLoading(true);
      // upload images and documents to S3 before making the request
      const imagesToUpload = uploadedPhotos?.length
        ? await Promise.all(Array.from(uploadedPhotos).map(async (file) => {
          const compressedFile = await compressImage(file);
          return {
            file: compressedFile,
            fileName: `/pocket-listing/images/${uuid()}-${file?.name}`,
            type: file?.type ?? "images/*",
          };
        }))
        : null;

      const documentsToUpload = uploadedDocuments?.length
        ? Array.from(uploadedDocuments).map((file) => ({
            file: file,
            fileName: `/pocket-listing/docs/${uuid()}-${file?.name}`,
            type: file?.type ?? "application/pdf",
          }))
        : null;

      if (imagesToUpload || payload?.photos?.length) {
        const toUpload = imagesToUpload ?? payload?.photos;
        console.log('toUpload', toUpload);
        await uploadManyToS3(toUpload, axios);
    }
    if (documentsToUpload || payload?.documents?.length) {
        const toUpload = documentsToUpload ?? payload?.documents;
        console.log('toUpload DOCS', toUpload);
        await uploadManyToS3(toUpload, axios);
      }

      if (!askingPrice) {
        showToast('Asking Price is a required field.', 'error');
        return;
      }
      const data = {
        ...payload,
        status: PropertyCardStatus.PocketListing,
        purpose: selectedPurpose?.value ?? null,
        agentValutionPrice: safeParseNumber(agencyValuation) > 0 ? safeParseNumber(agencyValuation) : null,
        askingPrice: askingPrice ?? null,
        lead: selectedLead?.value ?? null,
        currentAgent: payload?.currentAgent?.value ?? (authAgent?.id ?? authAgent?._id)  ?? null,
        beds: payload.beds,
        baths: parseInt(payload.baths).toString(),
        size: parseInt(payload.size).toString(),
        propertyType: payload?.propertyType?.value ?? null,
        building: payload?.building?.value ?? null,
        community: payload?.community?.value,
        pocketListNotes: payload?.pocketListNotes
      };
      if (imagesToUpload || payload?.photos?.length) {
        const toUpload = imagesToUpload ?? payload?.photos ?? [];
        data.photos = toUpload.map((image) => ({ original: image?.fileName }));
      }
      if (documentsToUpload || payload?.documents?.length) {
        const toUpload = documentsToUpload ?? payload?.documents ?? [];
        data.documents = toUpload.map((doc) => ({ type: "Other", original: doc?.fileName }));
      }
      console.log('Sending payload', data);
    //   return;
      const res = await axios.put(`/property-cards/${cardId}`, data);
      if (res.status === HttpStatus.OK) {
        toast.success("Updated property successfully!");
        updateCallback?.();
    }
} catch (error) {
    const serverErrorMessage = error.response.data.message;
    if (serverErrorMessage) {
        showToast(serverErrorMessage, 'error')
    } else {
        showToast("An error occurred, we couldn't update the property.", 'error')
      }
    } finally {
      setUpdateLoading(false);
      toggleFn();
    }
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      toggle={toggleFn}
      onClick={(e) => e.stopPropagation()}
    >
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            toggleFn();
          }}
          className="close mt-2"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">Convert Property Card to a Pocket Listing</h5>
          <div className="mt-4">
            <Row className="gy-4">
              <div>
              <Col sm="12">
                <Label tag="h5">Lead <span className="text-danger">*</span></Label>
                <RSelect
                  isRequired={true}
                  disabled={!!selectedLead}
                  placeholder='Select Lead...'
                  options={leadsOptions}
                  value={selectedLead}
                  cacheOptions={true}
                  isDisabled={!!selectedLead || leadsLoading}
                  isLoading={isInitialLeadsLoading}
                  loadOptionsFn={fetchLeadsByName}
                  onChange={(lead) => setSelectedLead(lead)}    
                />
              </Col>
              <Col sm="12" className="mt-4">
                <Label tag="h5">Purpose <span className="text-danger">*</span></Label>
                <RSelect
                  isRequired={true}
                  placeholder='Select Purpose...'
                  options={['Sell', 'Lease'].map((x) => ({
                    label: x,
                    value: x,
                  }))}
                  value={selectedPurpose}
                  onChange={(e) => {
                    setSelectedPurpose(e);
                  }}
                />
              </Col>
              <Row>
                <Col sm="6" className="mt-4">
                  <Label tag="h5">Asking Price <span className="text-danger">*</span></Label>
                  <Input
                    type="number"
                    placeholder="Asking Price"
                    multiple
                    id="askingPrice"
                    required={true}
                    value={askingPrice}
                    name="askingPrice"
                    onChange={(e) => {
                      setAskingPrice(e.target.value);
                    }}
                    />
                </Col>
                <Col sm="6" className="mt-4">
                  <Label tag="h5">Agency Valuation</Label>
                  <Input
                  type="number"
                  value={agencyValuation}
                  placeholder="Agent Valuation"
                  id="agentValutionPrice"
                  name="agentValutionPrice"
                  onChange={(e) => {
                    setAgencyValuation(e.target.value);
                  }}
                />
                </Col>
              </Row>
              <Row>
                {!hasValue(payload?.photos) ? <Col sm={hasValue(payload?.documents) && !hasValue(payload?.photos) ? "12" : "6"} className="mt-4">
                  <Label tag="h5">Photos</Label>
                  <Input
                    type="file"
                    placeholder="Upload Photos..."
                    multiple
                    id="photos"
                    name="photos"
                    accept="image/*"
                    onChange={(e) => {
                      if (!e.target.files || e.target.files.length === 0) return;
                      setUploadedPhotos(e.target.files);
                    }}
                    />
                </Col> : null}
                {!hasValue(payload?.documents) ? <Col sm={hasValue(payload?.photos) && !hasValue(payload?.documents) ? "12" : "6"} className="mt-4">
                  <Label tag="h5">Documents</Label>
                  <Input
                    type="file"
                    placeholder="Upload Documents..."
                    multiple
                    id="documents"
                    name="documents"
                    accept="application/pdf"
                    onChange={(e) => {
                      if (!e.target.files || e.target.files.length === 0) return;
                      setUploadedDocuments(e.target.files);
                    }}
                  />
                </Col> : null}
              </Row>
              </div>
              <Col sm="12" style={{ display: "flex", justifyContent: "end" }}>
                <Button
                  type="submit"
                  color="primary"
                  disabled={updateLoading}
                  onClick={() => updateCard()}
                >
                  {updateLoading ? (
                    <Spinner color="light" size={"sm"} />
                  ) : (
                    "Convert"
                  )}
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ConvertToPocketListingModal;
